/**
 * Pantalla "Nueva orden" (`POST /api/v1/orders`). Reemplaza el placeholder de
 * `ROUTES.ordersNew`. Equivale a `add_order.html` del SSR.
 *
 * Exige el permiso `add_orders` (mismo que la API y el menú del SSR): sin él, se
 * muestra un aviso en vez del formulario (la ruta ya cuelga de `RequireAuth`; el
 * gating de permiso lo hace la pantalla, como en el dashboard con `advanced_stats`).
 *
 * Orquesta el `OrderForm` (modo `create`) con el hook `useCreateOrder` (mutación
 * que invalida los listados). En éxito navega de vuelta a la lista de pendientes.
 * El `order_type` solo se ofrece si la sede activa permite láser
 * (`sedeLaserActivo`, espejo de `sede_allows_laser_orders`). Presentacional +
 * hooks; sin `fetch` (la sede va por `X-Sede-Id` en lib/api).
 */
import { useNavigate } from "react-router-dom";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui";
import { useAuth } from "@/lib/auth";
import { ROUTES } from "@/features/navigation";

import { dateInputToApi } from "../format";
import { useCreateOrder } from "../hooks/useCreateOrder";
import type { OrderFormValues } from "../schemas";
import type { OrderWriteBody, PaymentStatus } from "../types";
import { OrderForm } from "./OrderForm";

/** Permiso que gobierna el alta de órdenes (igual que el menú y la API). */
const ADD_ORDERS_PERMISSION = "add_orders";

/** Fecha de hoy en `YYYY-MM-DD` (valor por defecto de las fechas, como el SSR). */
function today(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Valores por defecto de una orden nueva (formato de UI). */
function defaultValues(): OrderFormValues {
  return {
    order_number: "",
    customer_name: "",
    attendant: "",
    sale_amount: "",
    invoice_date: today(),
    delivery_date: today(),
    delivery_time: "07:30",
    payment_status: "pendiente_de_pago" satisfies PaymentStatus,
    amount_paid: "",
    description: "",
    order_type: "normal",
  };
}

/**
 * Construye el cuerpo de `POST /orders` desde los valores validados del
 * formulario: convierte las fechas a `DD/MM/YYYY` y arrastra los campos que el
 * backend espera. `amount_paid` solo viaja con pago "abonado" (lo decide el form).
 */
function toCreateBody(values: Partial<OrderFormValues>): OrderWriteBody {
  const body: OrderWriteBody = {
    order_number: values.order_number ?? "",
    customer_name: values.customer_name ?? "",
    attendant: values.attendant ?? "",
    sale_amount: values.sale_amount ?? "",
    invoice_date: dateInputToApi(values.invoice_date ?? ""),
    delivery_date: dateInputToApi(values.delivery_date ?? ""),
    delivery_time: values.delivery_time ?? "",
    payment_status: (values.payment_status ?? "pendiente_de_pago") as PaymentStatus,
    description: values.description ?? "",
  };
  if (values.amount_paid !== undefined) {
    body.amount_paid = values.amount_paid;
  }
  if (values.order_type !== undefined) {
    body.order_type = values.order_type;
  }
  return body;
}

export function OrderNewScreen() {
  const navigate = useNavigate();
  const { hasPermission, sedeLaserActivo } = useAuth();
  const createOrder = useCreateOrder();

  if (!hasPermission(ADD_ORDERS_PERMISSION)) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Nueva orden</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-red-600" role="alert">
            No tienes permiso para crear órdenes.
          </p>
        </CardContent>
      </Card>
    );
  }

  function handleSubmit(editableValues: Partial<OrderFormValues>) {
    createOrder.mutate(toCreateBody(editableValues), {
      onSuccess: () => {
        navigate(ROUTES.ordersPending);
      },
    });
  }

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-2xl font-semibold text-slate-900">Nueva orden</h1>
      <Card>
        <CardContent>
          <OrderForm
            mode="create"
            initialValues={defaultValues()}
            // En alta todos los campos son editables (el permiso es de pantalla).
            canEditField={() => true}
            showOrderType={sedeLaserActivo}
            isSubmitting={createOrder.isPending}
            apiError={createOrder.isError ? createOrder.error : null}
            onSubmit={handleSubmit}
            submitLabel="Registrar"
            onCancel={() => navigate(ROUTES.ordersPending)}
          />
        </CardContent>
      </Card>
    </div>
  );
}
